<template>
  <div class="p-4">
    <div class="flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <h2 class="m-0">Alojamientos</h2>
      <div class="flex align-items-center gap-2">
        <Button
          icon="pi pi-link"
          label="Compartir enlace"
          size="small"
          severity="secondary"
          outlined
          @click="openAlojamientoLinkDialog"
        />
        <Button icon="pi pi-plus" label="Nuevo" size="small" @click="openCreateAlojamiento" />
      </div>
    </div>

    <div class="card">
      <DataTable :value="alojamientos" :loading="loadingAlojamientos" size="small" stripedRows>
        <Column field="nombre" header="Nombre" />
        <Column header="WhatsApp">
          <template #body="{ data }">
            <a v-if="data.whatsapp" :href="waLink(data.whatsapp)" target="_blank" rel="noopener">
              {{ data.whatsapp }}
            </a>
            <span v-else class="text-500">-</span>
          </template>
        </Column>
        <Column header="Ubicación">
          <template #body="{ data }">
            <span>{{ data.ubicacion || '-' }}</span>
            <a
              v-if="data.lat && data.lng"
              :href="`https://www.google.com/maps?q=${data.lat},${data.lng}`"
              target="_blank"
              rel="noopener"
              class="ml-2"
              v-tooltip="'Ver en el mapa'"
            >
              <i class="pi pi-map-marker" />
            </a>
          </template>
        </Column>
        <Column header="Cantidad" style="width: 100px; text-align:center">
          <template #body="{ data }">{{ data.cantidad }}</template>
        </Column>
        <Column header="Vehículo" style="width: 100px; text-align:center">
          <template #body="{ data }">
            <Tag :severity="data.vehiculo ? 'success' : 'secondary'" :value="data.vehiculo ? 'Sí' : 'No'" />
          </template>
        </Column>
        <Column header="Grupo de invitados">
          <template #body="{ data }">
            <span v-if="guestGroupLabel(data.guestGroupId)">{{ guestGroupLabel(data.guestGroupId) }}</span>
            <span v-else class="text-500">-</span>
          </template>
        </Column>
        <Column header="" style="width: 90px">
          <template #body="{ data }">
            <div class="flex gap-1 justify-content-end">
              <Button
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                text
                v-tooltip="'Editar'"
                @click="openEditAlojamiento(data)"
              />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar'"
                @click="confirmDeleteAlojamiento(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Dialog: crear/editar alojamiento -->
    <Dialog
      v-model:visible="showAlojamientoDialog"
      :header="editingAlojamiento ? 'Editar alojamiento' : 'Nuevo alojamiento'"
      modal
      style="width: 420px"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Nombre</label>
          <InputText v-model="alojamientoForm.nombre" placeholder="Nombre del anfitrión o lugar" autofocus />
        </div>
        <div class="flex flex-column gap-1">
          <label>WhatsApp</label>
          <InputText v-model="alojamientoForm.whatsapp" placeholder="+54 9 11 5555 5555" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Ubicación</label>
          <InputText v-model="alojamientoForm.ubicacion" placeholder="Dirección o referencia del lugar" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Coordenadas</label>
          <InputText v-model="coordenadasTexto" placeholder="-26.186765,-58.1836587" />
          <small v-if="coordenadasError" class="text-red-500">{{ coordenadasError }}</small>
          <small v-else-if="alojamientoForm.lat && alojamientoForm.lng" class="text-500">
            <a
              :href="`https://www.google.com/maps?q=${alojamientoForm.lat},${alojamientoForm.lng}`"
              target="_blank"
              rel="noopener"
            >Ver en el mapa</a>
          </small>
        </div>
        <div class="flex flex-column gap-1">
          <label>Cantidad de personas</label>
          <InputNumber v-model="alojamientoForm.cantidad" :min="0" showButtons />
        </div>
        <div class="flex align-items-center gap-2">
          <Checkbox v-model="alojamientoForm.vehiculo" :binary="true" inputId="alojamientoVehiculo" />
          <label for="alojamientoVehiculo">Tiene vehículo</label>
        </div>
        <div class="flex flex-column gap-1">
          <label>Grupo de invitados</label>
          <Select
            v-model="alojamientoForm.guestGroupId"
            :options="guestGroupOptions"
            option-label="label"
            option-value="id"
            placeholder="Sin grupo asignado"
            filter
            showClear
          />
          <small class="text-500">Un grupo de invitados puede repartirse en más de un alojamiento.</small>
        </div>
        <div v-if="errorAlojamiento" class="text-red-500 text-sm">{{ errorAlojamiento }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showAlojamientoDialog = false" />
          <Button label="Guardar" :loading="savingAlojamiento" @click="saveAlojamiento" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: enlace público para cargar alojamientos -->
    <Dialog v-model:visible="showAlojamientoLinkDialog" header="Compartir enlace de alojamientos" modal style="width: 480px">
      <div class="flex flex-column gap-3 pt-2">
        <small class="text-500">
          Cualquiera que reciba este enlace puede cargar un alojamiento sin necesitar una cuenta. No hace falta
          anotar el nombre de cada persona: alcanza con una persona de contacto por casa o alojamiento.
        </small>
        <div v-if="loadingAlojamientoLink" class="text-500 text-sm">Cargando enlace...</div>
        <div v-else class="flex align-items-center gap-2">
          <InputText :modelValue="alojamientoLinkUrl" readonly class="w-full" />
          <Button
            :icon="alojamientoLinkCopied ? 'pi pi-check' : 'pi pi-copy'"
            :severity="alojamientoLinkCopied ? 'success' : 'secondary'"
            @click="copyAlojamientoLink"
          />
        </div>
        <div class="flex justify-content-end">
          <Button label="Cerrar" severity="secondary" @click="showAlojamientoLinkDialog = false" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useAlojamientos } from '@/composables/useAlojamientos'
import { useGruposInvitados } from '@/composables/useGruposInvitados'
import { useConfirm } from 'primevue/useconfirm'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Checkbox from 'primevue/checkbox'
import Tag from 'primevue/tag'
import Select from 'primevue/dropdown'
import ConfirmDialog from 'primevue/confirmdialog'

const confirm = useConfirm()
const alojamientosApi = useAlojamientos()
const gruposInvitadosApi = useGruposInvitados()

const waLink = (w) => 'https://wa.me/' + String(w).replace(/\D/g, '')

const guestGroups = ref([])
const loadGuestGroups = async () => {
  const result = await gruposInvitadosApi.getGruposInvitados()
  guestGroups.value = result.guestGroups || []
}
const guestGroupOptions = computed(() =>
  guestGroups.value.map((g) => ({ id: g.id, label: g.encargado ? `${g.codigo} - ${g.encargado}` : g.codigo }))
)
const guestGroupLabel = (guestGroupId) => guestGroupOptions.value.find((g) => g.id === guestGroupId)?.label || ''

const alojamientos = ref([])
const loadingAlojamientos = ref(false)
const showAlojamientoDialog = ref(false)
const editingAlojamiento = ref(null)
const savingAlojamiento = ref(false)
const errorAlojamiento = ref(null)
const emptyAlojamientoForm = () => ({ nombre: '', whatsapp: '', ubicacion: '', lat: null, lng: null, cantidad: 0, vehiculo: false, guestGroupId: null })
const alojamientoForm = ref(emptyAlojamientoForm())

const coordenadasTexto = ref('')
const parseCoordenadas = (texto) => {
  const match = String(texto || '').trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/)
  if (!match) return null
  const lat = Number(match[1])
  const lng = Number(match[2])
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}
const coordenadasError = computed(() => {
  if (!coordenadasTexto.value.trim()) return null
  return parseCoordenadas(coordenadasTexto.value) ? null : 'Formato esperado: -26.186765,-58.1836587'
})
watch(coordenadasTexto, (texto) => {
  const parsed = parseCoordenadas(texto)
  if (parsed) {
    alojamientoForm.value.lat = parsed.lat
    alojamientoForm.value.lng = parsed.lng
  } else if (!texto.trim()) {
    alojamientoForm.value.lat = null
    alojamientoForm.value.lng = null
  }
})

const showAlojamientoLinkDialog = ref(false)
const loadingAlojamientoLink = ref(false)
const alojamientoLinkUrl = ref('')
const alojamientoLinkCopied = ref(false)

const loadAlojamientos = async () => {
  loadingAlojamientos.value = true
  try {
    const result = await alojamientosApi.getAlojamientos()
    alojamientos.value = result.alojamientos || []
  } finally {
    loadingAlojamientos.value = false
  }
}

const openCreateAlojamiento = () => {
  editingAlojamiento.value = null
  alojamientoForm.value = emptyAlojamientoForm()
  coordenadasTexto.value = ''
  errorAlojamiento.value = null
  showAlojamientoDialog.value = true
}

const openEditAlojamiento = (alojamiento) => {
  editingAlojamiento.value = alojamiento
  alojamientoForm.value = {
    nombre: alojamiento.nombre,
    whatsapp: alojamiento.whatsapp,
    ubicacion: alojamiento.ubicacion,
    lat: alojamiento.lat ?? null,
    lng: alojamiento.lng ?? null,
    cantidad: alojamiento.cantidad,
    vehiculo: alojamiento.vehiculo,
    guestGroupId: alojamiento.guestGroupId,
    updatedAt: alojamiento.updatedAt, // para concurrencia optimista al guardar
  }
  coordenadasTexto.value = alojamiento.lat != null && alojamiento.lng != null ? `${alojamiento.lat},${alojamiento.lng}` : ''
  errorAlojamiento.value = null
  showAlojamientoDialog.value = true
}

const saveAlojamiento = async () => {
  if (!alojamientoForm.value.nombre?.trim()) {
    errorAlojamiento.value = 'El nombre es requerido'
    return
  }
  savingAlojamiento.value = true
  errorAlojamiento.value = null
  try {
    const result = editingAlojamiento.value
      ? await alojamientosApi.updateAlojamiento(editingAlojamiento.value.id, alojamientoForm.value)
      : await alojamientosApi.createAlojamiento(alojamientoForm.value)
    if (result.error) {
      if (result.code === 'STALE') {
        errorAlojamiento.value = 'Otro usuario modificó este alojamiento. Se recargó la lista, revisá y volvé a guardar.'
        await loadAlojamientos()
        return
      }
      errorAlojamiento.value = result.error
      return
    }
    showAlojamientoDialog.value = false
    await loadAlojamientos()
  } finally {
    savingAlojamiento.value = false
  }
}

const confirmDeleteAlojamiento = (alojamiento) => {
  confirm.require({
    message: `¿Eliminar el alojamiento "${alojamiento.nombre}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await alojamientosApi.deleteAlojamiento(alojamiento.id)
      await loadAlojamientos()
    },
  })
}

const openAlojamientoLinkDialog = async () => {
  showAlojamientoLinkDialog.value = true
  alojamientoLinkCopied.value = false
  loadingAlojamientoLink.value = true
  try {
    let result = await alojamientosApi.getAlojamientoLink()
    if (!result.token) {
      result = await alojamientosApi.createAlojamientoLink()
    }
    alojamientoLinkUrl.value = alojamientosApi.getPublicUrl(result.token)
  } finally {
    loadingAlojamientoLink.value = false
  }
}

const copyAlojamientoLink = () => {
  navigator.clipboard.writeText(alojamientoLinkUrl.value)
  alojamientoLinkCopied.value = true
  setTimeout(() => { alojamientoLinkCopied.value = false }, 2000)
}

onMounted(() => {
  loadAlojamientos()
  loadGuestGroups()
})
</script>

<style scoped>
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
  text-align: left;
}
</style>
